import { ToolbarMenuCommandType, ToolbarMenuId } from '@src/suite/common/enums/enums';
import { Prompt } from '@src/suite/common/store/story/models/story.models';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { ProductionCommandsTitleKey, ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';

export interface MenuItem {
    type: MenuType;
    id: ToolbarMenuId | ProductionCommandType;
    fontSet?: string;
    icon?: string;
    labelKey?: I18nKeys;
    label?: string;
    options?: MenuItem[];
    storyTemplates?: StorytellingStoryRecord[];
    promptTemplates?: Prompt[];
    showIcon?: boolean;
    disabled?: boolean;
    commandType?: ToolbarMenuCommandType;
    isDirectAction?: boolean;
    metadata?: Pick<StorytellingRecord, 'Guid' | 'Type' | 'Path'> | Prompt | { name: string };
    isDelete?: boolean;
    checkboxValue?: boolean;
    selected?: boolean;
    addDeleteBtnToAction?: boolean;
    isRequired?: boolean;
}

type MenuType = 'action' | 'menu' | 'divider';

export const storyActions: MenuItem[] = [
    {
        type: 'menu',
        labelKey: I18nKeys.PSTEditorStoryMenu,
        id: ToolbarMenuId.STORY,
        options: [
            {
                type: 'menu',
                labelKey: I18nKeys.PSTEditorStoryMenuNew,
                id: ToolbarMenuId.STORY_NEW,
                icon: 'Add',
                showIcon: true,
                // options: dynamically generated based on StoryTemplates
            },
            {
                type: 'menu',
                labelKey: I18nKeys.PSTEditorStoryMenuGenerate,
                id: ToolbarMenuId.STORY_GENERATE,
                icon: 'stars',
                showIcon: true,
                // options: dynamically generated based on prompts
            },
            {
                type: 'menu',
                labelKey: I18nKeys.PSTEditorStoryMenuCopy,
                id: ToolbarMenuId.STORY_COPY,
                icon: 'content_copy',
                showIcon: true,
                options: [
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuCopyToClipboard,
                        id: ToolbarMenuId.STORY_COPY_TO_CLIPBOARD,
                        icon: 'content_paste',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.STORY,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuCopyStoryLink,
                        id: ToolbarMenuId.STORY_COPY_TO_STORY_LINK,
                        icon: 'link',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.STORY,
                    },
                ],
            },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuSave,
                id: ToolbarMenuId.STORY_SAVE,
                icon: 'save',
                showIcon: true,
                commandType: ToolbarMenuCommandType.STORY,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuSaveAsTemplate,
                id: ToolbarMenuId.STORY_SAVE_AS_TEMPLATE,
                icon: 'save_as',
                showIcon: true,
                commandType: ToolbarMenuCommandType.STORY,
            },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuRename,
                id: ToolbarMenuId.STORY_RENAME,
                icon: 'edit',
                showIcon: true,
                commandType: ToolbarMenuCommandType.STORY,
            },
            // {
            //     type: 'action',
            //     label: i18nKeys.PSTEditorStoryMenuMove,
            //     id: ToolbarMenuId.STORY_MOVE,
            //     icon: 'drive_file_move',
            //     showIcon: true,
            //     action: { type: ToolbarMenuCommandType.STORY, command: StoryCommandType.MOVE },
            // },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuDelete,
                id: ToolbarMenuId.DELETE,
                icon: 'delete',
                showIcon: true,
                commandType: ToolbarMenuCommandType.STORY,
                isDelete: true,
            },
        ],
    },
    {
        type: 'menu',
        labelKey: I18nKeys.PSTEditorStoryMenuEdit,
        id: ToolbarMenuId.STORY_EDIT,
        options: [
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuUndo,
                id: ToolbarMenuId.STORY_UNDO,
                icon: 'undo',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuRedo,
                id: ToolbarMenuId.STORY_REDO,
                icon: 'redo',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuCut,
                id: ToolbarMenuId.STORY_CUT,
                icon: 'content_cut',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuCopy,
                id: ToolbarMenuId.STORY_COPY,
                icon: 'content_copy',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuPaste,
            //     id: ToolbarMenuId.STORY_PASTE,
            //     icon: 'content_paste',
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuSelectAll,
                id: ToolbarMenuId.STORY_SELECT_ALL,
                icon: 'select_all',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            // {
            //     type: 'divider',
            //     id: ToolbarMenuId.DIVIDER,
            // },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuFindAndReplace,
            //     id: ToolbarMenuId.FIND_AND_REPLACE,
            //     icon: 'find_replace',
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
        ],
    },
    {
        type: 'menu',
        labelKey: I18nKeys.PSTEditorStoryMenuView,
        id: ToolbarMenuId.VIEW,
        options: [
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuListView,
            //     id: ToolbarMenuId.LIST_VIEW,
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuListSplitView,
            //     id: ToolbarMenuId.SPLIT_VIEW,
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
            // {
            //     type: 'divider',
            //     id: ToolbarMenuId.DIVIDER,
            // },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuListExpandAllElements,
                id: ToolbarMenuId.EXPAND_ALL_ELEMENTS,
                icon: 'unfold_more',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            // {
            //     type: 'divider',
            //     id: ToolbarMenuId.DIVIDER,
            // },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuListFullscreen,
            //     id: ToolbarMenuId.FULLSCREEN,
            //     icon: 'fullscreen',
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
        ],
    },
    {
        type: 'menu',
        id: ToolbarMenuId.INSERT,
        labelKey: I18nKeys.PSTEditorStoryMenuInsert,
        options: [
            // {
            //     type: 'action',
            //     labelKey: 'Image',
            //     icon: 'image',
            //     showIcon: true,
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Video',
            //     icon: 'videocam',
            //     showIcon: true,
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Voice recording',
            //     icon: 'mic',
            //     showIcon: true,
            // },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuVOSOT,
            //     id: ToolbarMenuId.VOSOT,
            //     icon: 'record_voice_over',
            //     showIcon: true,
            // },
            // {
            //     type: 'divider',
            //     id: ToolbarMenuId.DIVIDER,
            // },
            {
                type: 'action',
                labelKey: ProductionCommandsTitleKey[ProductionCommandType.ANCHOR],
                id: ProductionCommandType.ANCHOR,
                fontSet: 'material-symbols-outlined',
                icon: 'co_present',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'action',
                labelKey: ProductionCommandsTitleKey[ProductionCommandType.DURATION],
                id: ProductionCommandType.DURATION,
                icon: 'av_timer',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'action',
                labelKey: ProductionCommandsTitleKey[ProductionCommandType.INSERT],
                id: ProductionCommandType.INSERT,
                fontSet: 'st-icons',
                icon: 'mat-icon-insert',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            {
                type: 'action',
                labelKey: ProductionCommandsTitleKey[ProductionCommandType.TAKE],
                id: ProductionCommandType.TAKE,
                fontSet: 'st-icons',
                icon: 'mat-icon-take',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuTech,
            //     id: ProductionCommandType.TECH,
            //     icon: 'construction',
            //     showIcon: true,
            //     commandType: ToolbarMenuCommandType.EDITOR,
            // },
            {
                type: 'action',
                labelKey: ProductionCommandsTitleKey[ProductionCommandType.NOTES],
                id: ProductionCommandType.NOTES,
                fontSet: 'st-icons',
                icon: 'mat-icon-notes',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
            // {
            //     type: 'divider',
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Special Symbols',
            //     icon: 'emoji_symbols',
            //     showIcon: true,
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Equation',
            //     icon: 'functions',
            //     showIcon: true,
            // },
            // {
            //     type: 'divider',
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Watermark',
            //     icon: 'branding_watermark',
            //     showIcon: true,
            // },
            // {
            //     type: 'action',
            //     labelKey: 'Page Number',
            //     icon: 'tag',
            //     showIcon: true,
            // },
            // {
            //     type: 'divider',
            // },
            // {
            //     type: 'action',
            //     labelKey: i18nKeys.PSTEditorStoryMenuLink,
            //     id: ToolbarMenuId.LINK,
            //     icon: 'link',
            //     showIcon: true,
            // },
        ],
    },
    {
        type: 'menu',
        labelKey: I18nKeys.PSTEditorStoryMenuFormat,
        id: ToolbarMenuId.FORMAT,
        options: [
            {
                type: 'menu',
                labelKey: I18nKeys.PSTEditorStoryMenuText,
                id: ToolbarMenuId.TEXT,
                icon: 'text_fields',
                showIcon: true,
                options: [
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuBold,
                        id: ToolbarMenuId.BOLD,
                        icon: 'format_bold',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuItalic,
                        id: ToolbarMenuId.ITALIC,
                        icon: 'format_italic',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuUnderline,
                        id: ToolbarMenuId.UNDERLINE,
                        icon: 'format_underlined',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuStrikeThrough,
                        id: ToolbarMenuId.STRIKETHROUGH,
                        icon: 'format_strikethrough',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuSuperscript,
                        id: ToolbarMenuId.SUPERSCRIPT,
                        icon: 'superscript',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    {
                        type: 'action',
                        labelKey: I18nKeys.PSTEditorStoryMenuSubscript,
                        id: ToolbarMenuId.SUBSCRIPT,
                        icon: 'subscript',
                        showIcon: true,
                        commandType: ToolbarMenuCommandType.EDITOR,
                    },
                    // {
                    //     type: 'divider',
                    //     id: ToolbarMenuId.DIVIDER,
                    // },
                    // {
                    //     type: 'menu',
                    //     labelKey: i18nKeys.PSTEditorStoryMenuTextSize,
                    //     id: ToolbarMenuId.FONT_SIZE,
                    //     showIcon: true,
                    //     options: [
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuTextSizeIncreaseFontSize,
                    //             id: ToolbarMenuId.FONT_SIZE_INCREASE,
                    //             showIcon: false,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuTextSizeDecreaseFontSize,
                    //             id: ToolbarMenuId.FONT_SIZE_DECREASE,
                    //             showIcon: false,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //     ],
                    // },
                    // {
                    //     type: 'menu',
                    //     labelKey: i18nKeys.PSTEditorStoryMenuTextCapitalization,
                    //     id: ToolbarMenuId.CAPITALIZATION,
                    //     showIcon: true,
                    //     options: [
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuTextUppercase,
                    //             id: ToolbarMenuId.UPPERCASE,
                    //             showIcon: false,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuTextLowercase,
                    //             id: ToolbarMenuId.LOWERCASE,
                    //             showIcon: false,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuTextTitlecase,
                    //             id: ToolbarMenuId.TITLECASE,
                    //             showIcon: false,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //     ],
                    // },
                ],
            },

            {
                type: 'menu',
                labelKey: I18nKeys.PSTEditorStoryMenuParagraphStyles,
                id: ToolbarMenuId.PARAGRAPH_STYLES,
                icon: 'format_align_center',
                showIcon: true,
                options: [
                    {
                        type: 'menu',
                        labelKey: I18nKeys.PSTEditorStoryMenuHeadline1,
                        id: ToolbarMenuId.HEADLINE1,
                        options: [
                            {
                                type: 'action',
                                labelKey: I18nKeys.PSTEditorStoryMenuApplyForParagraph,
                                id: ToolbarMenuId.HEADLINE1,
                                commandType: ToolbarMenuCommandType.EDITOR,
                            },
                        ],
                    },
                    {
                        type: 'menu',
                        labelKey: I18nKeys.PSTEditorStoryMenuHeadline2,
                        id: ToolbarMenuId.HEADLINE2,
                        options: [
                            {
                                type: 'action',
                                labelKey: I18nKeys.PSTEditorStoryMenuApplyForParagraph,
                                id: ToolbarMenuId.HEADLINE2,
                                commandType: ToolbarMenuCommandType.EDITOR,
                            },
                        ],
                    },
                    {
                        type: 'menu',
                        labelKey: I18nKeys.PSTEditorStoryMenuHeadline3,
                        id: ToolbarMenuId.HEADLINE3,
                        options: [
                            {
                                type: 'action',
                                labelKey: I18nKeys.PSTEditorStoryMenuApplyForParagraph,
                                id: ToolbarMenuId.HEADLINE3,
                                commandType: ToolbarMenuCommandType.EDITOR,
                            },
                        ],
                    },
                    //         {
                    //             type: 'divider',
                    //         },
                    //         {
                    //             type: 'menu',
                    //             labelKey: 'Options',
                    //             options: [
                    //                 {
                    //                     type: 'action',
                    //                     labelKey: 'Save as My Default Styles',
                    //                 },
                    //                 {
                    //                     type: 'action',
                    //                     labelKey: 'Use My Default Styles',
                    //                 },
                    //                 {
                    //                     type: 'action',
                    //                     labelKey: 'Reset Styles',
                    //                 },
                    //             ],
                    //         },
                    // {
                    //     type: 'menu',
                    //     labelKey: i18nKeys.PSTEditorStoryMenuAlignAndIndent,
                    //     id: ToolbarMenuId.ALIGN_AND_INDENT,
                    //     icon: 'format_align_left',
                    //     showIcon: true,
                    //     options: [
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuAlignLeft,
                    //             id: ToolbarMenuId.ALIGN_LEFT,
                    //             icon: 'format_align_left',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuAlignCenter,
                    //             id: ToolbarMenuId.ALIGN_CENTER,
                    //             icon: 'format_align_center',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuAlignRight,
                    //             id: ToolbarMenuId.ALIGN_RIGHT,
                    //             icon: 'format_align_right',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuAlignJustify,
                    //             id: ToolbarMenuId.ALIGN_JUSTIFY,
                    //             icon: 'format_align_justify',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'divider',
                    //             id: ToolbarMenuId.DIVIDER,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuIndent,
                    //             id: ToolbarMenuId.INDENT,
                    //             icon: 'format_indent_increase',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    //         {
                    //             type: 'action',
                    //             labelKey: i18nKeys.PSTEditorStoryMenuOutdent,
                    //             id: ToolbarMenuId.OUTDENT,
                    //             icon: 'format_indent_decrease',
                    //             showIcon: true,
                    //             commandType: ToolbarMenuCommandType.EDITOR,
                    //         },
                    // {
                    //     type: 'action',
                    //     labelKey: 'Identation options',
                    //     showIcon: true,
                    // },
                ],
            },
            {
                type: 'divider',
                id: ToolbarMenuId.DIVIDER,
            },
            {
                type: 'action',
                labelKey: I18nKeys.PSTEditorStoryMenuClearFormatting,
                id: ToolbarMenuId.CLEAR_FORMATTING,
                icon: 'format_clear',
                showIcon: true,
                commandType: ToolbarMenuCommandType.EDITOR,
            },
        ],
    },
    {
        type: 'action',
        labelKey: I18nKeys.PSTEditorStoryMenuProperties,
        id: ToolbarMenuId.PROPERTIES,
        commandType: ToolbarMenuCommandType.STORY,
        isDirectAction: true,
    },

    // {
    //     type: 'menu',
    //     labelKey: 'Line and Paragraph Spacing',
    //     icon: 'format_line_spacing',
    //     showIcon: true,
    //     options: [
    //         {
    //             type: 'action',
    //             labelKey: 'Single',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: '1.15',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: '1.5',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Double',
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Add Space Before Paragraph',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Add Space After Paragraph',
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Custom Spacing',
    //         },
    //     ],
    // },
    // {
    //     type: 'menu',
    //     labelKey: 'Bullets and Numbering',
    //     icon: 'format_list_bulleted',
    //     showIcon: true,
    //     options: [
    //         {
    //             type: 'action',
    //             labelKey: 'List Options',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Numbered List',
    //         },
    //         {
    //             type: 'action',
    //             labelKey: 'Bucketed List',
    //         },
    //     ],
    // },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'menu',
    //             label: 'Table',
    //             icon: 'table_chart',
    //             showIcon: true,
    //             options: [
    //                 {
    //                     type: 'action',
    //                     label: 'Insert Table Above',
    //                     icon: 'add',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Insert Row Below',
    //                     icon: 'add',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Insert Column Left',
    //                     icon: 'add',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Insert Column Right',
    //                     icon: 'add',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Delete Row',
    //                     icon: 'delete',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Delete Column',
    //                     icon: 'delete',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Delete Table',
    //                     icon: 'delete',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Distribute Rows',
    //                     icon: 'expand',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Distribute Columns',
    //                     icon: 'expand',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Merge Cells',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Unmerge Cells',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Split Cell',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Sort Ascending',
    //                     icon: 'arrow_upward',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Sort Descending',
    //                     icon: 'arrow_downward',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'divider',
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Table Properties',
    //                     icon: 'table_chart',
    //                     showIcon: true,
    //                 },
    //             ],
    //         },
    //         {
    //             type: 'menu',
    //             label: 'Image',
    //             icon: 'image',
    //             showIcon: true,
    //             options: [
    //                 {
    //                     type: 'action',
    //                     label: 'Crop Image',
    //                     icon: 'crop',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Replace Image',
    //                     icon: 'image',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Reset Image',
    //                     icon: 'restart_alt',
    //                     showIcon: true,
    //                 },
    //                 {
    //                     type: 'action',
    //                     label: 'Image Options',
    //                     icon: 'settings',
    //                     showIcon: true,
    //                 },
    //             ],
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             label: 'Watermark',
    //             icon: 'branding_watermark',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Page Numbers',
    //             icon: 'tag',
    //             showIcon: true,
    //         },
    // {
    //     type: 'divider',
    //     id: ToolbarMenuId.DIVIDER,
    // },

    // ],
    // },
    // {
    //     type: 'action',
    //     label: 'Properties',
    // },
    // {
    //     type: 'menu',
    //     label: 'Tools',
    //     options: [
    //         {
    //             type: 'action',
    //             label: 'Spelling and Grammar',
    //             icon: 'spellcheck',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Word Count',
    //             icon: 'tag',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Review Suggested Edits',
    //             icon: 'edit',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Line Numbers',
    //             icon: 'format_list_numbered',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Dictionary',
    //             icon: 'manage_search',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             label: 'Translate Document',
    //             icon: 'translate',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Voice Typing',
    //             icon: 'mic',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             label: 'Notification Settings',
    //             icon: 'notifications',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Preferences',
    //             icon: 'manage_accounts',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Accessibility',
    //             icon: 'accessibility',
    //             showIcon: true,
    //         },
    //     ],
    // },
    // {
    //     type: 'menu',
    //     label: 'Help',
    //     options: [
    //         {
    //             type: 'action',
    //             label: 'Search the Menus',
    //             icon: 'search',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Help',
    //             icon: 'help',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Training',
    //             icon: 'school',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Updates',
    //             icon: 'update',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             label: 'Help Storytelling Improve',
    //             icon: 'construction',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'action',
    //             label: 'Report',
    //             icon: 'report',
    //             showIcon: true,
    //         },
    //         {
    //             type: 'divider',
    //         },
    //         {
    //             type: 'action',
    //             label: 'Keybaoard Shortcuts',
    //             icon: 'keyboard',
    //             showIcon: true,
    //         },
    //     ],
    // },
];
